import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Tab, Tabs, Form, Badge, ListGroup, Modal } from 'react-bootstrap';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import './Boutique.css';

const Boutique = () => {
  const { providerId } = useParams();
  const navigate = useNavigate();

  const [provider, setProvider] = useState(null);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [isFavorited, setIsFavorited] = useState(false);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewRating, setReviewRating] = useState(5);

  // Chat message state
  const [chatText, setChatText] = useState('');

  // Booking Wizard states
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [bookingDate, setBookingDate] = useState(new Date().toISOString().split('T')[0]);
  const [bookingTime, setBookingTime] = useState('10:00');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [bookingNotes, setBookingNotes] = useState('');
  const [guestsCount, setGuestsCount] = useState(1);
  const [selectedResourceName, setSelectedResourceName] = useState('');

  // Promo discount states
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');

  const token = localStorage.getItem('token');
  const loggedUser = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    if (showBookingModal && provider) {
      const providerActName = provider.activity && typeof provider.activity === 'object' 
        ? provider.activity.name 
        : provider.activity;
      let providerResources = provider.resources || [];
      if (providerResources.length === 0) {
        if (providerActName?.toLowerCase().includes('terrain')) {
          providerResources = [
            { name: 'Court A', capacity: 10 },
            { name: 'Court B', capacity: 10 },
            { name: 'Court C', capacity: 10 }
          ];
        } else {
          providerResources = [
            { name: 'Table 1', capacity: 2 },
            { name: 'Table 2', capacity: 2 },
            { name: 'Table 3', capacity: 4 },
            { name: 'Table 4', capacity: 4 },
            { name: 'Table 5', capacity: 6 }
          ];
        }
      }
      const matches = providerResources.filter(r => r.capacity >= guestsCount);
      if (matches.length > 0) {
        if (!selectedResourceName || !matches.some(m => m.name === selectedResourceName)) {
          setSelectedResourceName(matches[0].name);
        }
      } else {
        setSelectedResourceName('');
      }
    }
  }, [guestsCount, showBookingModal, provider]);

  useEffect(() => {
    loadBoutiqueData();
  }, [providerId]);

  const loadBoutiqueData = async () => {
    setLoading(true);
    try {
      // 1. Get Provider details
      const providerRes = await axios.get(`/api/auth/OneUser/${providerId}`);
      setProvider(providerRes.data.OneUser || providerRes.data);

      // 2. Get Services
      const servicesRes = await axios.get(`/api/services?provider=${providerId}`);
      setServices(servicesRes.data);

      // 3. Get Reviews
      const reviewsRes = await axios.get(`/api/reviews/${providerId}`);
      setReviews(reviewsRes.data);

      // 4. Get Promotions
      const promoRes = await axios.get(`/api/promotions/public?providerId=${providerId}`);
      setPromotions(promoRes.data);

      // 5. Get Messages (if logged in)
      if (token) {
        const messagesRes = await axios.get(`/api/messages/conversation/${providerId}`, {
          headers: { Authorization: token }
        });
        setMessages(messagesRes.data);

        // Fetch favorites
        const favRes = await axios.get('/api/auth/favorites', {
          headers: { Authorization: token }
        });
        const favoritedIds = (favRes.data || []).map(f => f._id || f);
        setIsFavorited(favoritedIds.includes(providerId));
      }
    } catch (err) {
      console.error("Error loading boutique data:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleFav = async () => {
    if (!token) {
      alert("Veuillez vous connecter pour ajouter des favoris.");
      return;
    }
    try {
      const res = await axios.post(`/api/auth/favorites/${providerId}`, {}, {
        headers: { Authorization: token }
      });
      const favoritedIds = (res.data.favorites || []);
      setIsFavorited(favoritedIds.includes(providerId));
    } catch (err) {
      console.error("Error toggling favorite:", err);
    }
  };

  const handleSendChat = async (e) => {
    e.preventDefault();
    if (!token) {
      alert("Veuillez vous connecter pour envoyer un message.");
      return;
    }
    if (!chatText.trim()) return;

    try {
      const res = await axios.post('/api/messages', {
        receiverId: providerId,
        text: chatText
      }, {
        headers: { Authorization: token }
      });
      setMessages(prev => [...prev, res.data]);
      setChatText('');
    } catch (err) {
      console.error("Error sending message:", err);
    }
  };

  const handleAddReview = async (e) => {
    e.preventDefault();
    if (!token) {
      alert("Veuillez vous connecter pour laisser un avis.");
      return;
    }

    try {
      await axios.post('/api/reviews', {
        rating: reviewRating,
        comment: reviewComment,
        providerId
      }, {
        headers: { Authorization: token }
      });

      setReviewComment('');
      setReviewRating(5);

      // Reload reviews and rating
      const reviewsRes = await axios.get(`/api/reviews/${providerId}`);
      setReviews(reviewsRes.data);
      const providerRes = await axios.get(`/api/auth/OneUser/${providerId}`);
      setProvider(providerRes.data.OneUser || providerRes.data);
    } catch (err) {
      console.error("Error adding review:", err);
    }
  };

  const handleApplyPromo = async () => {
    setPromoError('');
    setAppliedPromo(null);
    if (!promoCode.trim()) return;

    try {
      const res = await axios.post('/api/promotions/validate', {
        code: promoCode,
        providerId
      });

      if (res.data.valid) {
        setAppliedPromo(res.data.promotion);
      }
    } catch (err) {
      setPromoError(err.response?.data?.error || "Code promotionnel invalide.");
    }
  };

  const handleStartBooking = (service) => {
    if (!token) {
      alert("Veuillez vous connecter pour réserver.");
      navigate('/Login');
      return;
    }
    setSelectedService(service);
    setGuestsCount(1);
    if (loggedUser) {
      setClientName(loggedUser.name || '');
      setClientEmail(loggedUser.email || '');
      setClientPhone(loggedUser.phone?.toString() || '');
    }
    setShowBookingModal(true);
  };

  const handleConfirmBooking = async (e) => {
    e.preventDefault();
    if (!token) {
      alert("Veuillez vous connecter pour réserver.");
      return;
    }

    try {
      // Calculate final price with discount
      let basePrice = (selectedService.promotionPrice && selectedService.promotionPrice < selectedService.price)
        ? selectedService.promotionPrice
        : selectedService.price;
      let finalPrice = basePrice;
      if (appliedPromo) {
        if (appliedPromo.discountType === 'percentage') {
          finalPrice = basePrice - (basePrice * appliedPromo.value) / 100;
        } else {
          finalPrice = Math.max(0, basePrice - appliedPromo.value);
        }
      }

      const payload = {
        clientName,
        clientEmail,
        clientPhone,
        date: bookingDate,
        startTime: bookingTime,
        serviceIds: [selectedService._id],
        notes: bookingNotes,
        totalPrice: finalPrice,
        guestsCount: (provider?.bookingMode === 'resources' || provider?.activity?.name?.toLowerCase().includes('restaurant') || provider?.activity?.name?.toLowerCase().includes('terrain')) ? guestsCount : 1,
        selectedResourceName: selectedResourceName || undefined
      };

      const res = await axios.post('/api/appointments', payload, {
        headers: { Authorization: token }
      });

      setShowBookingModal(false);
      // Redirect to QRCodeConfirmation with the booking ID
      navigate(`/QRCodeConfirmation.js`, { state: { appointment: res.data } });
    } catch (err) {
      console.error("Error booking appointment:", err);
      alert(err.response?.data?.error || "Erreur lors de la réservation.");
    }
  };

  const calculateDiscountPrice = () => {
    if (!selectedService) return 0;
    const base = (selectedService.promotionPrice && selectedService.promotionPrice < selectedService.price)
      ? selectedService.promotionPrice
      : selectedService.price;
    if (!appliedPromo) return base;

    if (appliedPromo.discountType === 'percentage') {
      return base - (base * appliedPromo.value) / 100;
    }
    return Math.max(0, base - appliedPromo.value);
  };

  if (loading) {
    return <div className="text-center py-5 my-5">Chargement de la boutique...</div>;
  }

  if (!provider) {
    return <div className="text-center py-5 my-5 alert alert-danger">Boutique introuvable.</div>;
  }

  return (
    <div className="boutique-page">
      {/* Header Banner */}
      <div className="boutique-header py-5 text-white">
        <Container>
          <Row className="align-items-center">
            <Col md={8}>
              <Badge bg="warning" className="text-dark px-3 py-2 text-uppercase mb-3">
                {provider.activity && typeof provider.activity === 'object' ? provider.activity.name : provider.activity}
              </Badge>
              <div className="d-flex align-items-center gap-3 mb-2">
                <h1 className="display-4 font-weight-bold mb-0">{provider.businessName || provider.name}</h1>
                {token && (
                  <button 
                    onClick={toggleFav}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      fontSize: '32px',
                      cursor: 'pointer',
                      outline: 'none',
                      transition: 'transform 0.2s ease'
                    }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.2)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    {isFavorited ? '❤️' : '🤍'}
                  </button>
                )}
              </div>
              <p className="lead mb-3">
                {provider.description || "Découvrez notre catalogue de services exclusifs et prenez rendez-vous en ligne."}
              </p>
              <div className="d-flex flex-wrap gap-3 small align-items-center">
                <span>📍 {provider.adress || "Tunis, Tunisie"}</span>
                <span>📞 {provider.phone || "Non renseigné"}</span>
                <span>⭐ {provider.rating || 5.0} / 5.0 ({reviews.length} avis)</span>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="my-5">
        <Row>
          {/* Main content Area */}
          <Col lg={8}>
            <Tabs defaultActiveKey="catalog" className="mb-4 boutique-tabs p-2 bg-white rounded-3 shadow-sm">
              <Tab eventKey="catalog" title="🛠️ Services">
                <Card className="border-0 shadow-sm rounded-4 p-4 mb-4">
                  <h4 className="font-weight-bold text-secondary mb-4">Catalogue des services</h4>
                  
                  {services.length === 0 ? (
                    <p className="text-muted">Aucun service disponible pour le moment.</p>
                  ) : (
                    <ListGroup variant="flush">
                      {services.map(s => (
                        <ListGroup.Item key={s._id} className="py-4 px-0 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                          <div>
                            <h5 className="font-weight-bold text-dark mb-1">{s.name}</h5>
                            <p className="text-muted small mb-2">{s.description || 'Prestation de service de qualité.'}</p>
                            <span className="small text-muted">⏱️ {s.duration} min</span>
                          </div>
                          <div className="text-md-end d-flex flex-row flex-md-column justify-content-between align-items-center gap-2">
                            {s.promotionPrice && s.promotionPrice < s.price ? (
                              <div className="d-flex flex-column align-items-end">
                                <span className="text-muted text-decoration-line-through small">{s.price} DT</span>
                                <span className="font-weight-bold text-danger fs-5">{s.promotionPrice} DT</span>
                                <Badge bg="danger" className="small mt-1">{s.discountLabel || 'PROMO'}</Badge>
                              </div>
                            ) : (
                              <span className="font-weight-bold text-primary fs-5">{s.price} DT</span>
                            )}
                            <Button 
                              variant="outline-primary" 
                              className="rounded-pill px-4"
                              onClick={() => handleStartBooking(s)}
                            >
                              Réservez
                            </Button>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </Card>
              </Tab>

              <Tab eventKey="reviews" title="⭐ Avis Clients">
                <Card className="border-0 shadow-sm rounded-4 p-4 mb-4">
                  <h4 className="font-weight-bold text-secondary mb-4">Notes & Commentaires</h4>

                  {/* Submission Form */}
                  {token ? (
                    <Form onSubmit={handleAddReview} className="mb-5 bg-light p-4 rounded-3 border">
                      <h5 className="font-weight-bold mb-3">Laissez un avis</h5>
                      <Form.Group className="mb-3">
                        <Form.Label>Note (1 à 5 étoiles)</Form.Label>
                        <Form.Select 
                          value={reviewRating}
                          onChange={e => setReviewRating(parseInt(e.target.value))}
                          className="w-auto"
                        >
                          <option value={5}>⭐⭐⭐⭐⭐ (5/5)</option>
                          <option value={4}>⭐⭐⭐⭐ (4/5)</option>
                          <option value={3}>⭐⭐⭐ (3/5)</option>
                          <option value={2}>⭐⭐ (2/5)</option>
                          <option value={1}>⭐ (1/5)</option>
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Votre commentaire</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          placeholder="Partagez votre expérience avec cet établissement..."
                          value={reviewComment}
                          onChange={e => setReviewComment(e.target.value)}
                          required
                        />
                      </Form.Group>
                      <Button variant="primary" type="submit" className="rounded-pill px-4">
                        Publier mon avis
                      </Button>
                    </Form>
                  ) : (
                    <div className="alert alert-warning mb-4">
                      Veuillez vous <Link to="/Login">connecter</Link> pour laisser un avis.
                    </div>
                  )}

                  {reviews.length === 0 ? (
                    <p className="text-muted">Aucun commentaire pour cet établissement. Soyez le premier à donner votre avis !</p>
                  ) : (
                    <ListGroup variant="flush">
                      {reviews.map(r => (
                        <ListGroup.Item key={r._id} className="py-3 px-0">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <strong className="text-dark">{r.client?.name || 'Client Bookly'}</strong>
                            <span className="text-warning font-weight-bold">
                              {"⭐".repeat(r.rating)}
                            </span>
                          </div>
                          <p className="text-muted mb-1" style={{ fontSize: '14.5px' }}>{r.comment}</p>
                          <small className="text-muted" style={{ fontSize: '12px' }}>
                            Publié le {format(parseISO(r.createdAt), 'dd MMMM yyyy', { locale: fr })}
                          </small>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </Card>
              </Tab>

              <Tab eventKey="chat" title="💬 Discussion">
                <Card className="border-0 shadow-sm rounded-4 p-4 mb-4">
                  <h4 className="font-weight-bold text-secondary mb-4">Chat direct avec {provider.businessName || provider.name}</h4>
                  
                  {token ? (
                    <div className="chat-box-container d-flex flex-column" style={{ height: '400px' }}>
                      <div className="chat-messages flex-grow-1 overflow-y-auto mb-3 p-3 bg-light rounded border" style={{ maxHeight: '300px' }}>
                        {messages.length === 0 ? (
                          <div className="text-center py-5 text-muted">
                            Aucun message. Posez une question au prestataire directement !
                          </div>
                        ) : (
                          messages.map(m => {
                            const isMe = m.sender === loggedUser._id;
                            return (
                              <div key={m._id} className={`d-flex mb-2 ${isMe ? 'justify-content-end' : 'justify-content-start'}`}>
                                <div 
                                  className={`p-3 rounded-3 max-width-70 ${isMe ? 'bg-primary text-white' : 'bg-secondary-subtle text-dark'}`}
                                  style={{ fontSize: '14.5px' }}
                                >
                                  {m.text}
                                  <div className="text-end small mt-1 opacity-75" style={{ fontSize: '10px' }}>
                                    {format(parseISO(m.createdAt), 'HH:mm', { locale: fr })}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                      <Form onSubmit={handleSendChat} className="d-flex gap-2">
                        <Form.Control
                          type="text"
                          placeholder="Tapez votre message ici..."
                          value={chatText}
                          onChange={e => setChatText(e.target.value)}
                          className="rounded-pill py-2"
                        />
                        <Button variant="primary" type="submit" className="rounded-pill px-4">Envoyer</Button>
                      </Form>
                    </div>
                  ) : (
                    <div className="alert alert-warning">
                      Veuillez vous <Link to="/Login">connecter</Link> pour discuter avec le prestataire.
                    </div>
                  )}
                </Card>
              </Tab>
            </Tabs>
          </Col>

          {/* Sidebar Area */}
          <Col lg={4}>
            {/* Promotions / Coupons */}
            <Card className="border-0 shadow-sm rounded-4 p-4 mb-4">
              <Card.Title className="font-weight-bold text-secondary mb-3">🎁 Offres & Coupons</Card.Title>
              {promotions.length === 0 ? (
                <p className="text-muted small mb-0">Aucune offre promotionnelle en cours.</p>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {promotions.map(p => (
                    <div key={p._id} className="promo-coupon p-3 rounded border border-dashed border-primary bg-primary-subtle text-center">
                      <span className="badge bg-primary mb-2 text-uppercase" style={{ fontSize: '13px', letterSpacing: '1px' }}>
                        {p.code}
                      </span>
                      <h5 className="font-weight-bold text-dark mb-1">
                        -{p.value}{p.discountType === 'percentage' ? '%' : ' DT'} de remise
                      </h5>
                      <p className="text-muted small mb-0">Valable sur tout le catalogue</p>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Info details */}
            <Card className="border-0 shadow-sm rounded-4 p-4">
              <Card.Title className="font-weight-bold text-secondary mb-3">ℹ️ Établissement</Card.Title>
              <ul className="list-unstyled mb-0 d-flex flex-column gap-3">
                <li>
                  <strong>Secteur :</strong><br/>
                  <span className="text-muted">{provider.activity && typeof provider.activity === 'object' ? provider.activity.name : provider.activity}</span>
                </li>
                <li>
                  <strong>Adresse complète :</strong><br/>
                  <span className="text-muted">{provider.adress || "Tunis, Tunisie"}</span>
                </li>
                <li>
                  <strong>Téléphone :</strong><br/>
                  <span className="text-muted">{provider.phone || "Non renseigné"}</span>
                </li>
              </ul>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Booking Wizard Modal */}
      {selectedService && (
        <Modal show={showBookingModal} onHide={() => setShowBookingModal(false)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>📅 Réservation de service</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleConfirmBooking}>
            <Modal.Body>
              <Row>
                {/* Left side info */}
                <Col md={5} className="border-end">
                  <div className="p-3 bg-light rounded-3 mb-3">
                    <h5 className="font-weight-bold text-dark">{selectedService.name}</h5>
                    <p className="text-muted small mb-2">{selectedService.description}</p>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="small text-muted">Durée:</span>
                      <strong className="small">{selectedService.duration} min</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="small text-muted">Prix :</span>
                      <strong className="small text-primary">
                        {(selectedService.promotionPrice && selectedService.promotionPrice < selectedService.price)
                          ? `${selectedService.promotionPrice} DT (Tarif Promo)`
                          : `${selectedService.price} DT`
                        }
                      </strong>
                    </div>
                  </div>

                  {/* Promo application */}
                  <div className="p-3 bg-white border rounded-3">
                    <Form.Group className="mb-3">
                      <Form.Label className="small font-weight-bold">Code Promotionnel</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="text"
                          placeholder="CODE"
                          value={promoCode}
                          onChange={e => setPromoCode(e.target.value)}
                          className="text-uppercase"
                        />
                        <Button variant="outline-primary" size="sm" onClick={handleApplyPromo}>
                          Appliquer
                        </Button>
                      </div>
                      {promoError && <div className="text-danger small mt-1">{promoError}</div>}
                      {appliedPromo && (
                        <div className="text-success small mt-2">
                          ✓ Code appliqué : Remise de {appliedPromo.value}{appliedPromo.discountType === 'percentage' ? '%' : ' DT'}
                        </div>
                      )}
                    </Form.Group>

                    <div className="border-top pt-3 d-flex justify-content-between align-items-center">
                      <span>Total à régler :</span>
                      <h4 className="font-weight-bold text-success mb-0">
                        {calculateDiscountPrice()} DT
                      </h4>
                    </div>
                  </div>
                </Col>

                {/* Right side form */}
                <Col md={7}>
                  {/* Informations de profil pré-remplies en arrière-plan */}
                  <Form.Group className="mb-3">
                    <Form.Label>Téléphone *</Form.Label>
                    <Form.Control
                      type="tel"
                      value={clientPhone}
                      onChange={e => setClientPhone(e.target.value)}
                      placeholder="Entrez votre numéro de téléphone"
                      required
                    />
                    <Form.Text className="text-muted">
                      Vous pouvez modifier ce numéro s'il est différent de celui enregistré dans votre profil.
                    </Form.Text>
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Date de rendez-vous *</Form.Label>
                        <Form.Control
                          type="date"
                          value={bookingDate}
                          onChange={e => setBookingDate(e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Heure *</Form.Label>
                        <Form.Control
                          type="time"
                          value={bookingTime}
                          onChange={e => setBookingTime(e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {(() => {
                    const providerActName = provider?.activity && typeof provider.activity === 'object' 
                      ? provider.activity.name 
                      : provider?.activity;
                    const isResourceBased = provider?.bookingMode === 'resources' || 
                      providerActName?.toLowerCase().includes('restaurant') || 
                      providerActName?.toLowerCase().includes('terrain');
                    const resourceLabel = provider?.resourceName || 
                      (providerActName?.toLowerCase().includes('terrain') ? 'Terrain' : 'Table');
                    const maxCap = provider?.resources && provider.resources.length > 0 
                      ? Math.max(...provider.resources.map(r => r.capacity), 1) 
                      : 10;

                    if (!isResourceBased) return null;

                    let providerResources = provider?.resources || [];
                    if (providerResources.length === 0) {
                      if (providerActName?.toLowerCase().includes('terrain')) {
                        providerResources = [
                          { name: 'Court A', capacity: 10 },
                          { name: 'Court B', capacity: 10 },
                          { name: 'Court C', capacity: 10 }
                        ];
                      } else {
                        providerResources = [
                          { name: 'Table 1', capacity: 2 },
                          { name: 'Table 2', capacity: 2 },
                          { name: 'Table 3', capacity: 4 },
                          { name: 'Table 4', capacity: 4 },
                          { name: 'Table 5', capacity: 6 }
                        ];
                      }
                    }

                    const matchingResources = providerResources.filter(r => r.capacity >= guestsCount);

                    return (
                      <>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">
                            Nombre de personnes *
                          </Form.Label>
                          <Form.Select 
                            value={guestsCount} 
                            onChange={e => setGuestsCount(parseInt(e.target.value))}
                          >
                            {Array.from({ length: maxCap }, (_, i) => i + 1).map(num => (
                              <option key={num} value={num}>
                                {num} personne{num > 1 ? 's' : ''}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">
                            Choix de la {resourceLabel} / Espace *
                          </Form.Label>
                          <Form.Select 
                            value={selectedResourceName} 
                            onChange={e => setSelectedResourceName(e.target.value)}
                            required
                          >
                            <option value="">-- Choisir un(e) {resourceLabel.toLowerCase()} --</option>
                            {matchingResources.map((r, i) => (
                              <option key={i} value={r.name}>
                                {r.name} (Capacité max: {r.capacity} pers)
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Text className="text-muted">
                            Sélectionnez la {resourceLabel.toLowerCase()} souhaitée parmi celles adaptées à votre groupe.
                          </Form.Text>
                        </Form.Group>
                      </>
                    );
                  })()}

                  <Form.Group className="mb-3">
                    <Form.Label>Notes (optionnel)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Ex: demandes ou préférences particulières..."
                      value={bookingNotes}
                      onChange={e => setBookingNotes(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowBookingModal(false)}>Annuler</Button>
              <Button variant="primary" type="submit">Confirmer la Réservation</Button>
            </Modal.Footer>
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default Boutique;
